import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Textarea,
  IconButton,
  Spinner,
  useColorModeValue,
  Button,
  VStack,
  Avatar,
  Badge,
  Tooltip,
  Divider,
} from '@chakra-ui/react';
import {
  MdSend,
  MdDeleteOutline,
  MdAdd,
  MdFileDownload,
  MdAutoAwesome,
  MdChatBubbleOutline,
} from 'react-icons/md';
import AiApi from '../../../api/ai';

// ─── Typing indicator ─────────────────────────────────────────────────────────

function TypingDots() {
  return (
    <Flex align="center" gap="4px" py="2px">
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          w="7px"
          h="7px"
          borderRadius="full"
          bg="brand.400"
          style={{
            animation: `typing-bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes typing-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-6px); opacity: 1; }
        }
      `}</style>
    </Flex>
  );
}

// ─── Markdown renderer ────────────────────────────────────────────────────────

function MarkdownText({ text }) {
  const codeBlockRegex = /```[\s\S]*?```/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = codeBlockRegex.exec(text)) !== null) {
    if (match.index > lastIndex)
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    parts.push({
      type: 'code',
      content: match[0].replace(/^```[^\n]*\n?/, '').replace(/```$/, ''),
    });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push({ type: 'text', content: text.slice(lastIndex) });

  const renderInline = (line) => {
    const boldRegex = /\*\*(.*?)\*\*/g;
    const result = [];
    let li = 0,
      m;
    while ((m = boldRegex.exec(line)) !== null) {
      if (m.index > li) result.push(line.slice(li, m.index));
      result.push(<strong key={m.index}>{m[1]}</strong>);
      li = m.index + m[0].length;
    }
    if (li < line.length) result.push(line.slice(li));
    return result;
  };

  const renderTextBlock = (content, idx) => {
    const lines = content.split('\n');
    const elements = [];
    let tableLines = [],
      inTable = false;

    const flushTable = () => {
      if (!tableLines.length) return;
      const rows = tableLines.filter((l) => !l.match(/^\|[-| ]+\|$/));
      const headers = rows[0]?.split('|').filter((c) => c.trim() !== '') ?? [];
      elements.push(
        <Box key={`table-${elements.length}`} overflowX="auto" my={3} borderRadius="lg" overflow="hidden" boxShadow="sm">
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th
                    key={i}
                    style={{
                      padding: '8px 14px',
                      background: 'linear-gradient(135deg, #7551FF 0%, #422AFB 100%)',
                      color: 'white',
                      textAlign: 'right',
                      fontWeight: 600,
                      fontSize: '12px',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {h.trim()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(1).map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? '#f8f9ff' : 'white' }}>
                  {row
                    .split('|')
                    .filter((c) => c.trim() !== '')
                    .map((cell, ci) => (
                      <td
                        key={ci}
                        style={{
                          border: '1px solid #e8eaf6',
                          padding: '7px 14px',
                          textAlign: 'right',
                          fontSize: '13px',
                        }}
                      >
                        {cell.trim()}
                      </td>
                    ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      );
      tableLines = [];
      inTable = false;
    };

    lines.forEach((line, i) => {
      if (line.startsWith('|')) {
        inTable = true;
        tableLines.push(line);
        return;
      }
      if (inTable) flushTable();
      if (line.startsWith('### '))
        elements.push(
          <Text key={i} fontWeight="700" fontSize="sm" mt={3} mb={1} color="brand.600">
            {renderInline(line.slice(4))}
          </Text>
        );
      else if (line.startsWith('## '))
        elements.push(
          <Text key={i} fontWeight="700" fontSize="md" mt={3} mb={1}>
            {renderInline(line.slice(3))}
          </Text>
        );
      else if (line.startsWith('# '))
        elements.push(
          <Text key={i} fontWeight="800" fontSize="lg" mt={3} mb={1}>
            {renderInline(line.slice(2))}
          </Text>
        );
      else if (line.match(/^[-*] /))
        elements.push(
          <Flex key={i} align="flex-start" gap={2} mb="2px">
            <Text color="brand.500" fontWeight="bold" mt="1px">•</Text>
            <Text>{renderInline(line.slice(2))}</Text>
          </Flex>
        );
      else if (line.trim() === '') elements.push(<Box key={i} h="8px" />);
      else elements.push(<Text key={i}>{renderInline(line)}</Text>);
    });
    if (inTable) flushTable();
    return <Box key={idx}>{elements}</Box>;
  };

  return (
    <Box>
      {parts.map((part, idx) =>
        part.type === 'code' ? (
          <Box
            key={idx}
            as="pre"
            bg="gray.900"
            color="green.300"
            p={4}
            my={2}
            borderRadius="lg"
            fontSize="12px"
            overflowX="auto"
            fontFamily="monospace"
            whiteSpace="pre-wrap"
          >
            {part.content}
          </Box>
        ) : (
          renderTextBlock(part.content, idx)
        )
      )}
    </Box>
  );
}

// ─── Suggested prompts ────────────────────────────────────────────────────────

const SUGGESTIONS = [
  { icon: '📊', text: 'כמה העברות היו היום?' },
  { icon: '👥', text: 'אילו חברות פעילות כרגע?' },
  { icon: '⚠️', text: 'האם יש כשלים בפייפליינים?' },
  { icon: '📥', text: 'תייצא לי את העברות החודש לאקסל' },
];

// ─── Main component ───────────────────────────────────────────────────────────

export default function AIAssistant() {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  const bgPage = useColorModeValue('white', 'navy.800');
  const bgSidebar = useColorModeValue('gray.50', 'navy.900');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.100', 'navy.600');
  const activeConvBg = useColorModeValue('brand.50', 'navy.700');
  const hoverBg = useColorModeValue('gray.100', 'navy.700');
  const mutedColor = useColorModeValue('gray.400', 'gray.500');
  const inputBg = useColorModeValue('gray.50', 'navy.900');
  const sidebarEmptyIconBg = useColorModeValue('gray.100', 'navy.700');
  const dividerColor = useColorModeValue('gray.200', 'navy.600');
  const msgAreaBg = useColorModeValue('gray.50', 'navy.900');
  const aiBubbleBg = useColorModeValue('white', 'navy.700');
  const typingBubbleBg = useColorModeValue('white', 'navy.700');

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const loadConversations = useCallback(async () => {
    try {
      const res = await AiApi.listConversations();
      setConversations(res.data ?? []);
    } catch {}
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const openConversation = async (id) => {
    if (id === activeId) return;
    setLoadingConv(true);
    setError(null);
    try {
      const res = await AiApi.getConversation(id);
      setActiveId(id);
      setMessages(res.data.messages ?? []);
    } catch {
      setError('שגיאה בטעינת השיחה.');
    } finally {
      setLoadingConv(false);
    }
  };

  const newConversation = async () => {
    try {
      const res = await AiApi.createConversation();
      const conv = res.data;
      setConversations((prev) => [conv, ...prev]);
      setActiveId(conv.id);
      setMessages([]);
      setInput('');
      setError(null);
      setTimeout(() => textareaRef.current?.focus(), 100);
    } catch {
      setError('שגיאה ביצירת שיחה חדשה.');
    }
  };

  const deleteConversation = async (e, id) => {
    e.stopPropagation();
    try {
      await AiApi.deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeId === id) {
        setActiveId(null);
        setMessages([]);
      }
    } catch {}
  };

  const sendMessage = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || loading || !activeId) return;

    const optimisticMsg = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, optimisticMsg]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const res = await AiApi.chat(activeId, trimmed);
      const { reply, file } = res.data;
      setMessages((prev) => [...prev, { role: 'assistant', content: reply, file: file ?? null }]);
      setConversations((prev) =>
        prev
          .map((c) =>
            c.id === activeId
              ? {
                  ...c,
                  title:
                    c.title === 'שיחה חדשה' ? trimmed.slice(0, 50) : c.title,
                  updatedAt: new Date().toISOString(),
                }
              : c
          )
          .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
      );
    } catch {
      setError('שגיאה בחיבור לשרת. נסה שוב.');
      setMessages((prev) => prev.filter((m) => m !== optimisticMsg));
    } finally {
      setLoading(false);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const downloadFile = (file) => {
    const mimeType = file.name.endsWith('.docx')
      ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const bytes = Uint8Array.from(atob(file.base64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    if (d.toDateString() === now.toDateString())
      return d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' });
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: '16px', md: '32px' }} pb="20px">
      <Flex
        h={{ base: 'calc(100vh - 210px)', md: 'calc(100vh - 180px)' }}
        overflow="hidden"
        borderRadius="2xl"
        overflow="hidden"
        boxShadow="0px 8px 40px rgba(0,0,0,0.08)"
        border="1px solid"
        borderColor={borderColor}
      >
        {/* ── Sidebar ─────────────────────────────────────────────────── */}
        <Flex
          direction="column"
          w={{ base: '220px', lg: '270px' }}
          flexShrink={0}
          bg={bgSidebar}
          borderLeft="2px solid"
          borderColor={dividerColor}
          dir="rtl"
        >
          {/* Sidebar header */}
          <Box
            p={4}
            background="linear-gradient(135deg, #7551FF 0%, #422AFB 100%)"
          >
            <Flex align="center" gap={2} mb={3}>
              <Box
                w="32px"
                h="32px"
                borderRadius="lg"
                bg="whiteAlpha.200"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <MdAutoAwesome color="white" size="18px" />
              </Box>
              <Box>
                <Text color="white" fontWeight="700" fontSize="sm" lineHeight="1.2">
                  איתן AI
                </Text>
                <Text color="whiteAlpha.700" fontSize="10px">
                  עוזר חכם לפיירו
                </Text>
              </Box>
            </Flex>
            <Button
              w="100%"
              size="sm"
              bg="whiteAlpha.200"
              color="white"
              _hover={{ bg: 'whiteAlpha.300' }}
              _active={{ bg: 'whiteAlpha.400' }}
              leftIcon={<MdAdd />}
              onClick={newConversation}
              borderRadius="lg"
              fontWeight="600"
              fontSize="sm"
            >
              שיחה חדשה
            </Button>
          </Box>

          {/* Conversation list */}
          <VStack spacing={0} align="stretch" overflowY="auto" flex={1} py={2}>
            {conversations.length === 0 ? (
              <Flex direction="column" align="center" justify="center" gap={2} mt={8} px={4}>
                <Box
                  w="40px"
                  h="40px"
                  borderRadius="xl"
                  bg={sidebarEmptyIconBg}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <MdChatBubbleOutline size="20px" color="#A0AEC0" />
                </Box>
                <Text fontSize="xs" color={mutedColor} textAlign="center">
                  אין שיחות עדיין.{'\n'}פתח שיחה חדשה להתחלה.
                </Text>
              </Flex>
            ) : (
              conversations.map((conv) => (
                <Box
                  key={conv.id}
                  px={3}
                  py="10px"
                  cursor="pointer"
                  bg={conv.id === activeId ? activeConvBg : 'transparent'}
                  _hover={{ bg: conv.id === activeId ? activeConvBg : hoverBg }}
                  onClick={() => openConversation(conv.id)}
                  borderRight={conv.id === activeId ? '3px solid' : '3px solid transparent'}
                  borderColor={conv.id === activeId ? 'brand.500' : 'transparent'}
                  transition="all 0.15s"
                  role="group"
                >
                  <Flex align="center" justify="space-between">
                    <Box flex={1} minW={0}>
                      <Text
                        fontSize="sm"
                        fontWeight={conv.id === activeId ? '600' : '400'}
                        color={conv.id === activeId ? 'brand.600' : textColor}
                        noOfLines={1}
                      >
                        {conv.title}
                      </Text>
                      <Text fontSize="10px" color={mutedColor} mt="1px">
                        {formatTime(conv.updatedAt)}
                      </Text>
                    </Box>
                    <Tooltip label="מחק שיחה" placement="left" hasArrow>
                      <IconButton
                        icon={<MdDeleteOutline />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="מחק"
                        onClick={(e) => deleteConversation(e, conv.id)}
                        opacity={0}
                        _groupHover={{ opacity: 1 }}
                        transition="opacity 0.15s"
                        ms={1}
                        borderRadius="md"
                      />
                    </Tooltip>
                  </Flex>
                </Box>
              ))
            )}
          </VStack>

          {/* Sidebar footer */}
          <Box p={3} borderTop="1px solid" borderColor={borderColor}>
            <Text fontSize="10px" color={mutedColor} textAlign="center">
              מבוסס על Claude Sonnet · קריאה בלבד
            </Text>
          </Box>
        </Flex>

        {/* ── Chat panel ──────────────────────────────────────────────── */}
        <Flex direction="column" flex={1} minW={0} bg={bgPage}>
          {/* Header */}
          <Flex
            px={5}
            py={3}
            borderBottom="1px solid"
            borderColor={borderColor}
            align="center"
            justify="space-between"
            bg={bgPage}
          >
            <Flex align="center" gap={3}>
              <Box
                w="36px"
                h="36px"
                borderRadius="xl"
                background="linear-gradient(135deg, #7551FF 0%, #422AFB 100%)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <MdAutoAwesome color="white" size="18px" />
              </Box>
              <Box>
                <Text fontWeight="700" color={textColor} fontSize="sm">
                  איתן AI
                </Text>
                <Flex align="center" gap={1}>
                  <Box w="6px" h="6px" borderRadius="full" bg="green.400" />
                  <Text fontSize="10px" color="green.500" fontWeight="500">
                    מוכן לסייע
                  </Text>
                </Flex>
              </Box>
            </Flex>
            {activeId && (
              <Badge colorScheme="purple" variant="subtle" borderRadius="lg" fontSize="10px" px={2}>
                שיחה #{activeId}
              </Badge>
            )}
          </Flex>

          {/* Messages area */}
          <Box
            flex={1}
            overflowY="auto"
            p={6}
            dir="rtl"
            bg={msgAreaBg}
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '4px' },
            }}
          >
            {/* No conversation selected */}
            {!activeId && !loadingConv && (
              <Flex h="100%" align="center" justify="center" direction="column" gap={6}>
                <Box textAlign="center">
                  <Box
                    w="64px"
                    h="64px"
                    borderRadius="2xl"
                    background="linear-gradient(135deg, #7551FF 0%, #422AFB 100%)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mx="auto"
                    mb={4}
                    boxShadow="0px 8px 24px rgba(117, 81, 255, 0.3)"
                  >
                    <MdAutoAwesome color="white" size="30px" />
                  </Box>
                  <Text fontWeight="700" fontSize="lg" color={textColor} mb={1}>
                    ברוך הבא לאיתן AI
                  </Text>
                  <Text fontSize="sm" color={mutedColor} mb={6}>
                    עוזר חכם לניהול נתוני פיירו
                  </Text>
                  <Button
                    colorScheme="brand"
                    leftIcon={<MdAdd />}
                    onClick={newConversation}
                    borderRadius="xl"
                    size="md"
                    background="linear-gradient(135deg, #7551FF 0%, #422AFB 100%)"
                    _hover={{ opacity: 0.9, transform: 'translateY(-1px)', boxShadow: '0px 6px 20px rgba(117,81,255,0.4)' }}
                    transition="all 0.2s"
                  >
                    התחל שיחה חדשה
                  </Button>
                </Box>

                {/* Suggestion chips */}
                <Box w="100%" maxW="500px">
                  <Text fontSize="xs" color={mutedColor} textAlign="center" mb={3} fontWeight="500">
                    שאלות נפוצות
                  </Text>
                  <Flex wrap="wrap" gap={2} justify="center">
                    {SUGGESTIONS.map((s) => (
                      <Button
                        key={s.text}
                        size="sm"
                        variant="outline"
                        color={textColor}
                        borderRadius="xl"
                        fontWeight="400"
                        fontSize="xs"
                        sx={{ borderColor: '#A0AEC0 !important' }}
                        _hover={{ bg: activeConvBg, borderColor: 'brand.300 !important' }}
                        onClick={async () => {
                          await newConversation();
                        }}
                        leftIcon={<Text>{s.icon}</Text>}
                      >
                        {s.text}
                      </Button>
                    ))}
                  </Flex>
                </Box>
              </Flex>
            )}

            {/* Loading conversation */}
            {loadingConv && (
              <Flex h="100%" align="center" justify="center" direction="column" gap={3}>
                <Spinner color="brand.500" size="lg" thickness="3px" />
                <Text fontSize="sm" color={mutedColor}>
                  טוען שיחה...
                </Text>
              </Flex>
            )}

            {/* Welcome message for empty conversation */}
            {activeId && !loadingConv && messages.length === 0 && (
              <Box>
                <Flex justify="flex-end" mb={5} align="flex-end" gap={3}>
                  <Box
                    maxW="75%"
                    bg={aiBubbleBg}
                    color={textColor}
                    px={5}
                    py={4}
                    borderRadius="18px 18px 4px 18px"
                    fontSize="sm"
                    lineHeight="tall"
                    boxShadow="0px 2px 12px rgba(0,0,0,0.06)"
                  >
                    <MarkdownText text={'שלום! אני **איתן**, עוזר ה-AI של פיירו.\n\nאני יכול לעזור לך עם:\n- חיפוש עובדים וחברות\n- נתוני העברות ודוחות\n- מצב אינטגרציות ופייפליינים\n- ייצוא קבצי Excel ו-Word\n\nבמה אוכל לעזור לך?'} />
                  </Box>
                  <Box
                    w="32px"
                    h="32px"
                    borderRadius="xl"
                    background="linear-gradient(135deg, #7551FF 0%, #422AFB 100%)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    boxShadow="0px 4px 12px rgba(117,81,255,0.3)"
                  >
                    <MdAutoAwesome color="white" size="14px" />
                  </Box>
                </Flex>

                <Divider mb={5} />
                <Text fontSize="xs" color={mutedColor} textAlign="center" mb={3} fontWeight="500">
                  התחל בשאלה אחת מהאלה
                </Text>
                <Flex wrap="wrap" gap={2} justify="center">
                  {SUGGESTIONS.map((s) => (
                    <Button
                      key={s.text}
                      size="sm"
                      variant="outline"
                      color="brand.600"
                      borderRadius="xl"
                      fontWeight="400"
                      fontSize="xs"
                      sx={{ borderColor: '#7551FF !important' }}
                      _hover={{ bg: 'brand.50', borderColor: 'brand.400 !important' }}
                      onClick={() => {
                        setInput(s.text);
                        setTimeout(() => textareaRef.current?.focus(), 50);
                      }}
                      leftIcon={<Text>{s.icon}</Text>}
                    >
                      {s.text}
                    </Button>
                  ))}
                </Flex>
              </Box>
            )}

            {/* Messages */}
            {messages.map((msg, idx) => (
              <Flex
                key={idx}
                justify={msg.role === 'user' ? 'flex-start' : 'flex-end'}
                mb={4}
                align="flex-end"
                gap={3}
              >
                {msg.role === 'assistant' && (
                  <Box
                    w="30px"
                    h="30px"
                    borderRadius="lg"
                    background="linear-gradient(135deg, #7551FF 0%, #422AFB 100%)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    boxShadow="0px 3px 10px rgba(117,81,255,0.25)"
                  >
                    <MdAutoAwesome color="white" size="13px" />
                  </Box>
                )}

                <Box
                  maxW="75%"
                  bg={
                    msg.role === 'user'
                      ? 'linear-gradient(135deg, #7551FF 0%, #422AFB 100%)'
                      : aiBubbleBg
                  }
                  color={msg.role === 'user' ? 'white' : textColor}
                  px={5}
                  py={msg.role === 'assistant' ? 4 : 3}
                  borderRadius={msg.role === 'user' ? '18px 18px 18px 4px' : '18px 18px 4px 18px'}
                  fontSize="sm"
                  lineHeight="tall"
                  boxShadow={
                    msg.role === 'user'
                      ? '0px 4px 16px rgba(117,81,255,0.3)'
                      : '0px 2px 12px rgba(0,0,0,0.06)'
                  }
                >
                  {msg.role === 'assistant' ? (
                    <>
                      <MarkdownText text={msg.content} />
                      {msg.file && (
                        <Button
                          mt={3}
                          size="sm"
                          colorScheme="green"
                          leftIcon={<MdFileDownload />}
                          onClick={() => downloadFile(msg.file)}
                          borderRadius="lg"
                          variant="solid"
                        >
                          הורד {msg.file.name}
                        </Button>
                      )}
                    </>
                  ) : (
                    <Text whiteSpace="pre-wrap">{msg.content}</Text>
                  )}
                </Box>

                {msg.role === 'user' && (
                  <Avatar size="xs" name="א" bg="gray.300" color="gray.700" flexShrink={0} />
                )}
              </Flex>
            ))}

            {/* Typing indicator */}
            {loading && (
              <Flex justify="flex-end" mb={4} align="flex-end" gap={3}>
                <Box
                  w="30px"
                  h="30px"
                  borderRadius="lg"
                  background="linear-gradient(135deg, #7551FF 0%, #422AFB 100%)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  boxShadow="0px 3px 10px rgba(117,81,255,0.25)"
                >
                  <MdAutoAwesome color="white" size="13px" />
                </Box>
                <Box
                  bg={aiBubbleBg}
                  px={5}
                  py={4}
                  borderRadius="18px 18px 4px 18px"
                  boxShadow="0px 2px 12px rgba(0,0,0,0.06)"
                >
                  <TypingDots />
                </Box>
              </Flex>
            )}

            {/* Error */}
            {error && (
              <Flex justify="center" mb={4}>
                <Box
                  bg="red.50"
                  border="1px solid"
                  borderColor="red.200"
                  px={4}
                  py={3}
                  borderRadius="xl"
                  maxW="400px"
                  textAlign="center"
                >
                  <Text color="red.500" fontSize="sm" fontWeight="500">
                    {error}
                  </Text>
                </Box>
              </Flex>
            )}

            <Box ref={bottomRef} />
          </Box>

          {/* ── Input area ──────────────────────────────────────────── */}
          <Box
            borderTop="1px solid"
            borderColor={borderColor}
            p={4}
            bg={bgPage}
          >
            <Flex gap={3} align="flex-end" dir="rtl">
              <Box flex={1} position="relative">
                <Textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    activeId ? 'שאל שאלה... (Enter לשליחה, Shift+Enter לשורה חדשה)' : 'פתח שיחה חדשה כדי להתחיל'
                  }
                  resize="none"
                  rows={3}
                  bg={inputBg}
                  borderRadius="xl"
                  fontSize="sm"
                  dir="rtl"
                  isDisabled={loading || !activeId}
                  sx={{ borderColor: '#CBD5E0 !important' }}
                  _focus={{
                    borderColor: 'brand.400',
                    boxShadow: '0 0 0 2px rgba(117,81,255,0.15)',
                    bg: bgPage,
                  }}
                  _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
                  transition="all 0.2s"
                  pr={4}
                />
              </Box>
              <Flex direction="column" gap={2} align="center">
                <Tooltip label="שלח (Enter)" placement="top" hasArrow>
                  <IconButton
                    aria-label="שלח"
                    icon={<MdSend />}
                    background="linear-gradient(135deg, #7551FF 0%, #422AFB 100%)"
                    color="white"
                    borderRadius="xl"
                    onClick={() => sendMessage()}
                    isLoading={loading}
                    isDisabled={!input.trim() || !activeId}
                    size="lg"
                    _hover={{
                      opacity: 0.9,
                      transform: 'translateY(-1px)',
                      boxShadow: '0px 6px 20px rgba(117,81,255,0.4)',
                    }}
                    _active={{ transform: 'translateY(0px)' }}
                    _disabled={{ opacity: 0.4, cursor: 'not-allowed', transform: 'none', boxShadow: 'none' }}
                    transition="all 0.2s"
                  />
                </Tooltip>
              </Flex>
            </Flex>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
}
